/**
 * ExportPanel — format selector + options for assembly exports.
 *
 * Supports FCPXML (primary), EDL, XML (Premiere), and AAF.
 * Includes configurable markers, bins, and media path prefix for relinking.
 */

import { useState } from 'react'
import { Download, ChevronDown, ChevronUp, Settings2 } from 'lucide-react'
import toast from 'react-hot-toast'
import apiClient from '../services/api'

type ExportFormat = 'fcpxml' | 'edl' | 'xml' | 'aaf'

interface FormatOption {
  value: ExportFormat
  label: string
  nle: string
  description: string
  recommended?: boolean
}

const FORMATS: FormatOption[] = [
  { value: 'fcpxml', label: 'FCPXML', nle: 'FCP / Resolve', description: 'Full bins, markers, relink paths', recommended: true },
  { value: 'edl', label: 'EDL', nle: 'Universal', description: 'CMX3600 with relink data' },
  { value: 'xml', label: 'XML', nle: 'Premiere Pro', description: 'XMEML v4 interchange' },
  { value: 'aaf', label: 'AAF', nle: 'Avid MC', description: 'Binary with bins and markers' },
]

interface ExportPanelProps {
  assemblyId: number
  assemblyName: string
  clipCount: number
  totalDuration: number
}

export default function ExportPanel({
  assemblyId,
  assemblyName,
  clipCount,
  totalDuration,
}: ExportPanelProps) {
  const [format, setFormat] = useState<ExportFormat>('fcpxml')
  const [showOptions, setShowOptions] = useState(false)
  const [includeMarkers, setIncludeMarkers] = useState(true)
  const [includeBins, setIncludeBins] = useState(true)
  const [mediaPathPrefix, setMediaPathPrefix] = useState(() => {
    const ua = navigator.userAgent
    if (ua.includes('Win')) return 'C:\\Media'
    if (ua.includes('Linux')) return '/home/media'
    return '/Volumes/Media'
  })
  const [exporting, setExporting] = useState(false)

  const supportsOptions = format === 'fcpxml' || format === 'edl' || format === 'aaf'
  const supportsBins = format === 'fcpxml' || format === 'aaf'

  const handleExport = async () => {
    setExporting(true)
    try {
      const params: Record<string, string | boolean> = {}
      if (format === 'fcpxml' || format === 'edl' || format === 'aaf') {
        params.include_markers = includeMarkers
        params.media_path_prefix = mediaPathPrefix
      }
      if (format === 'fcpxml' || format === 'aaf') {
        params.include_bins = includeBins
      }

      const res = await apiClient.get(`/assemblies/${assemblyId}/export/${format}`, {
        params,
        responseType: 'blob',
      })

      const blob = new Blob([res.data])
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const safeName = assemblyName.replace(/[^a-zA-Z0-9 _-]/g, '').trim() || 'assembly'
      const ext = format === 'fcpxml' ? 'fcpxml' : format
      a.download = `${safeName}.${ext}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      toast.success(`${format.toUpperCase()} exported`)
    } catch {
      toast.error(`Failed to export ${format.toUpperCase()}`)
    } finally {
      setExporting(false)
    }
  }

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60)
    const sec = Math.floor(s % 60)
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`
  }

  return (
    <div className="space-y-3">
      {/* Format selector + options + export row */}
      <div className="flex items-center gap-2">
        <div className="relative">
          <select
            value={format}
            onChange={e => setFormat(e.target.value as ExportFormat)}
            disabled={exporting}
            className="appearance-none bg-white/10 border border-white/15 rounded-lg pl-3 pr-8 py-1.5 text-xs text-[var(--rq-text)] disabled:opacity-50 focus:outline-none focus:border-white/35 cursor-pointer"
          >
            {FORMATS.map(f => (
              <option key={f.value} value={f.value}>
                {f.label} — {f.nle}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--rq-text-soft)] pointer-events-none" />
        </div>

        {supportsOptions && (
          <button
            onClick={() => setShowOptions(!showOptions)}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] bg-white/5 border border-white/10 rounded-md transition-colors"
          >
            <Settings2 className="w-3 h-3" />
            Options
            {showOptions ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        )}

        <div className="flex-1" />

        <span className="text-[10px] text-[var(--rq-text-soft)]">
          {clipCount} clip{clipCount !== 1 ? 's' : ''} &middot; {formatDuration(totalDuration)}
        </span>

        <button
          onClick={handleExport}
          disabled={exporting || clipCount === 0}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium rounded-md bg-[var(--rq-blue)] text-white hover:bg-[var(--rq-blue)]/80 transition-colors disabled:opacity-50"
        >
          <Download className="w-3.5 h-3.5" />
          {exporting ? 'Exporting...' : `Export ${format.toUpperCase()}`}
        </button>
      </div>

      {clipCount === 0 && (
        <p className="text-[11px] text-[var(--rq-text-soft)]">
          Export is disabled until at least one clip is selected in this draft.
        </p>
      )}

      {/* Options panel */}
      {showOptions && supportsOptions && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2.5">
          <label className="flex items-center gap-2 text-xs text-[var(--rq-text-soft)] cursor-pointer">
            <input
              type="checkbox"
              checked={includeMarkers}
              onChange={e => setIncludeMarkers(e.target.checked)}
              className="rounded border-white/20 bg-white/10 text-[var(--rq-blue)] focus:ring-0 w-3.5 h-3.5"
            />
            Include markers (notes, tags, titles)
          </label>

          {supportsBins && (
            <label className="flex items-center gap-2 text-xs text-[var(--rq-text-soft)] cursor-pointer">
              <input
                type="checkbox"
                checked={includeBins}
                onChange={e => setIncludeBins(e.target.checked)}
                className="rounded border-white/20 bg-white/10 text-[var(--rq-blue)] focus:ring-0 w-3.5 h-3.5"
              />
              Include bin structure (folder hierarchy)
            </label>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--rq-text-soft)] mb-1 block">
              Media path prefix (for relinking)
            </label>
            <input
              value={mediaPathPrefix}
              onChange={e => setMediaPathPrefix(e.target.value)}
              placeholder="/Volumes/Media"
              className="w-full rq-input-dark text-xs"
            />
          </div>
        </div>
      )}
    </div>
  )
}
